<script setup>
import { ref, computed, watch } from 'vue'
import Heading from '@sections/home/heading.vue'
import Button from '@components/global/button.vue'
import ArrowButton from '@components/global/arrow-button.vue'
import { Swiper, SwiperSlide } from 'swiper/vue'

import { useProductStore } from '@stores/product'
const store = useProductStore()
const selectedIndex = ref(0)

// Swiper instance
const swiperInstance = ref(null)

function onSwiper(swiper) {
	swiperInstance.value = swiper
}

watch(selectedIndex, (newIndex) => {
	const sw = swiperInstance.value
	if (!sw) return

	const start = sw.activeIndex
	const perView = sw.params.slidesPerView === 'auto' ? sw.slidesPerViewDynamic() : sw.params.slidesPerView
	const end = start + perView - 1

	if (newIndex < start || newIndex > end) {
		sw.slideTo(newIndex)
	}
})

const selectedProduct = computed(() => store.products[selectedIndex.value])

const currentNumber = computed(() => String(selectedIndex.value + 1).padStart(2, '0'))
const totalNumber = computed(() => String(store.products.length).padStart(2, '0'))

function nextProduct() {
	selectedIndex.value = (selectedIndex.value + 1) % store.products.length
}

function prevProduct() {
	selectedIndex.value = (selectedIndex.value - 1 + store.products.length) % store.products.length
}

const breakpoints = ref({
	0: {
		slidesPerView: 3,
	},
	500: {
		slidesPerView: 5,
	},
})
</script>

<template>
	<section class="container flex flex-col">
		<Heading>
			<template #title>featured products</template>
			<template #text>Excepteur do minim eu ad eu anim quis qui elit non ullamco.</template>
			<template #button>
				<Button class="btn-outline" :to="{ name: 'products' }">view all products</Button>
			</template>
		</Heading>

		<div class="mt-10 block w-fit self-center md:hidden">
			<Button :to="{ name: 'products' }" class="btn-outline">view all products</Button>
		</div>

		<div class="relative flex flex-col justify-between pt-16 xl:flex-row">
			<div class="order-1 flex w-[350px] flex-col gap-6 font-medium uppercase" v-if="selectedProduct">
				<!-- TODO type -->
				<!-- <span class="font-body text-body-sm text-text-secondary-muted">
					{{ selectedProduct.type }}
				</span> -->
				<div class="h-[200px] w-[350px] font-heading text-[80px] font-medium uppercase leading-[64px] text-text-secondary md:h-auto md:text-display-sm md:leading-[83.20px]">
					{{ selectedProduct.name }}
				</div>

				<!-- TODO description -->
				<!-- <div class="mt-10 block w-[70%] self-end font-body text-body-sm text-text-secondary-muted md:mt-20">
					Excepteur do minim eu ad eu anim quis qui elit non ullamco.
					<br />
					Consectetur proident anim id pariatur minim laboris aute irure dolor enim sunt nostrud.
				</div> -->
			</div>

			<img src="https://hennep.s3.amazonaws.com/above/images/home/products/aurora.png" alt="" class="absolute -bottom-[50%] -left-[50%] hidden w-full rotate-[-135deg] mix-blend-screen xl:block" />

			<img src="https://hennep.s3.amazonaws.com/above/images/home/products/aurora.png" alt="" class="absolute -right-[80%] bottom-0 hidden w-full rotate-[-160.36deg] bg-gradient-to-br from-black/0 to-black mix-blend-screen xl:block" />

			<img src="https://hennep.s3.amazonaws.com/above/images/home/products/planet.png" alt="" class="absolute -right-[10%] bottom-0 hidden w-[230px] rotate-45 xl:block" />

			<div class="relative order-3 mt-10 rounded-full border border-dashed border-border-muted md:mt-0 xl:order-2" v-if="selectedProduct">
				<img src="https://hennep.s3.amazonaws.com/above/images/home/products/planet3.png" class="grayscale-100 h-full w-full" alt="" />

				<img :src="selectedProduct.images?.at(0)?.url" alt="Product Image" class="absolute-center w-[30%] object-cover" />
				<img src="https://hennep.s3.amazonaws.com/above/images/home/products/planet2.png" class="absolute bottom-10 right-10 w-[20%] brightness-50" alt="" />

				<div class="absolute-x-center -top-2 rotate-45 border border-green-200 bg-transparent p-1">
					<div class="center h-1.5 w-1.5 bg-green-200"></div>
				</div>
			</div>

			<div class="order-3 flex flex-col items-center gap-16 self-center">
				<h2 class="hidden items-center gap-3 text-sf-purple-base md:flex">
					{{ currentNumber }}
					<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" class="text-sf-purple-base">
						<rect x="6" y="6" width="6" height="6" fill="currentColor" />
						<rect x="12" width="6" height="6" fill="currentColor" />
						<rect y="12" width="6" height="6" fill="currentColor" />
					</svg>
					{{ totalNumber }}
				</h2>
				<div class="hidden md:block">
					<Button class="btn-outline big" :to="{ name: 'products' }">
						<h3>get abducted</h3>
					</Button>
				</div>

				<div class="flex items-center gap-4 p-2">
					<Button @click="prevProduct" aria-label="Previous product" class="group rotate-180 hover:scale-110">
						<ArrowButton />
					</Button>
					<Button @click="nextProduct" aria-label="Next product" class="group hover:scale-110">
						<ArrowButton />
					</Button>
				</div>
			</div>
		</div>

		<div class="h-[68px] w-[400px] overflow-hidden sm:self-center md:block">
			<Swiper :breakpoints="breakpoints" :spaceBetween="16" @swiper="onSwiper" class="h-full">
				<SwiperSlide v-for="(product, index) in store.products" :key="product.id" class="flex items-center justify-center gap-4">
					<Button @click.prevent="selectedIndex = index" :class="['relative inline-flex h-16 w-16 items-center justify-center rounded opacity-90 outline-dashed outline-[1.5px] outline-offset-[-0.75px] outline-sf-purple-base', selectedIndex === index ? 'bg-sf-purple-base/10' : 'bg-bg-dark']" :key="product.id" :href="product.link">
						<div :class="['absolute left-[54px] top-[6px] h-2 w-2 rounded-[1px]', selectedIndex === index ? 'bg-sf-purple-base' : 'bg-transparent']"></div>
						<img :src="product.images?.at(0)?.url" alt="" class="h-11 w-7" />
					</Button>
				</SwiperSlide>
			</Swiper>
		</div>

		<div class="mt-10 hidden w-fit self-center md:block">
			<Button :to="{ name: 'products' }" class="btn-outline">view all products</Button>
		</div>

		<div class="mt-10 block md:hidden">
			<Button class="btn-outline big" :to="{ name: 'products' }">
				<h3>get abducted</h3>
			</Button>
		</div>
	</section>
</template>

<style scoped></style>
